import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAudit } from "./AuditContext";
import { tapFeedback } from "./haptics";
import { PresenterFloorShadow } from "./PresenterFloorShadow";
import { useIsMobileAudit } from "./useIsMobileAudit";

/**
 * Rehearsal Room · Room 05 — SPACE
 * =================================
 * Interactive 3x3 perspective grid on the taped floor. User places the
 * Presenter for 4 presentation moments (Opening, Tension, Video, CTA),
 * then watches a replay of the exact path they created, then reflects.
 *
 * No scoring, no "correct" cell — this room only captures the pattern.
 */

// ---------- CONSTANTS -------------------------------------------------------
const EASE = [0.16, 1, 0.3, 1];

const INTRO_STEPS = [
  "Now let\u2019s use the whole stage.",
  "Different moments can ask for different positions.",
  "I\u2019ll give you four presentation moments.",
  "For each one, choose where you would stand.",
  "There\u2019s no correct map.",
  "Choose what feels natural to you.",
];

// Per-question bubble sequences (shown before the grid becomes interactive).
const Q_STEPS = {
  opening: [
    "You\u2019re about to begin.",
    "You walk onto the stage.",
    "Where would you stand to say your first words?",
  ],
  tension: [
    "Now you introduce the main problem or tension.",
    "Where would you deliver that moment?",
  ],
  video: [
    "A video starts playing on the projector.",
    "Where would you stand while the audience watches it?",
  ],
  cta: [
    "The video ends.",
    "You reach your call to action.",
    "Where would you stand to ask the audience to act?",
  ],
};

const MOMENTS = ["opening", "tension", "video", "cta"];

// Stable reference for the "no bubbles" case — used when qStep>=4 to avoid a
// fresh [] literal in the deps of the bubble/canProceed effect (would trigger
// an infinite render loop when the AuditContext setters produce new state).
const EMPTY_BUBBLES = Object.freeze([]);

// Standing pose per moment.
const MOMENT_POSE = {
  opening: "welcomeOpen",
  tension: "guidePoint",
  video:   "listening",
  cta:     "presentingWide",
};

// Replay stop bubbles — heading + short supporting line.
const REPLAY_LABELS = {
  opening: { heading: "Opening",             sub: "Your first words land here." },
  tension: { heading: "Problem / Tension",   sub: "This is where the challenge enters the story." },
  video:   { heading: "Video",               sub: "Here you make space for the audience to watch." },
  cta:     { heading: "Call to Action",      sub: "This is where you ask the audience to act." },
};

// Bubbles right after Q4 pick, before the auto-replay starts.
const REPLAY_INTRO_STEPS = [
  "Good.",
  "You\u2019ve created a stage path.",
  "Now let\u2019s watch it back.",
];

// Bubbles after the replay finishes, before the reflection cards.
const REFLECTION_INTRO_STEPS = [
  "Take a look at the path you created.",
  "Different moments led you to different choices.",
  "What guided those choices most?",
];

const REFLECTION_QUESTION = "What guided those choices most?";

const REFLECTION_OPTIONS = [
  { id: "meaning",   label: "The meaning of each moment" },
  { id: "attention", label: "Keeping the audience\u2019s attention" },
  { id: "stage",     label: "Making room for what was happening on stage" },
  { id: "natural",   label: "What felt most natural to me" },
];

const OUTRO_STEPS = [
  "Good.",
  "Movement becomes useful when it has a reason.",
  "And staying still can be a deliberate choice too.",
  "Once you know why you\u2019re moving, the stage becomes easier to use.",
  "Let\u2019s keep going.",
];

// ---------- GRID GEOMETRY ---------------------------------------------------
// SVG viewBox is 0 0 100 100 and maps directly to the scene container.
// Grid polygons — traced onto the tape marks baked into
// /audit/bg_space.webp (1672 × 941). Corner + interior divider positions
// measured by pixel-sampling the actual tape lines in the image.
//
//   Trapezoid outer corners:
//     TL (407, 605)   TR (1258, 605)
//     BL (114, 843)   BR (1531, 843)
//
//   Interior horizontal row dividers: y = 664 and y = 741.
//   (Row heights 59 / 77 / 102 px reflect the perspective foreshortening.)
//
//   Column dividers are computed as EVEN THIRDS along each row line
//   (the baked grid is essentially evenly divided; measured column-
//   divider positions were within a few px of the equal-thirds points).
const GRID_VB_W = 1672;
const GRID_VB_H = 941;

// Trapezoid outer corners (measured, shifted +20 px forward per approval).
const _TL = [407, 625], _TR = [1258, 625];
const _BL = [170, 863], _BR = [1500, 863];

// Horizontal row Y values (measured tape lines + 20 px forward shift).
const _Y_A = 625; // back / top
const _Y_B = 684; // row 1|2 divider
const _Y_C = 761; // row 2|3 divider
const _Y_D = 863; // front / bottom

// Column dividers are defined as STRAIGHT LINES from back-row X to
// front-row X. Back-row divs are perfect even thirds along the top tape
// line. Front-row divs are the measured baked positions (col 8 is
// slightly wider than cols 7 & 9 — the baked grid isn't perfectly even
// at the front). Intermediate row heights interpolate along each line.
const _COL_BACK_1  = _TL[0] + (_TR[0] - _TL[0]) * (1 / 3); // = 690.7
const _COL_BACK_2  = _TL[0] + (_TR[0] - _TL[0]) * (2 / 3); // = 974.3
const _COL_FRONT_1 = 608; // measured baked interior 7|8 divider
const _COL_FRONT_2 = 1063; // measured baked interior 8|9 divider

// x-position of column divider `k` (1 or 2) at row height y.
const _colDivX = (k, y) => {
  const t = (y - _Y_A) / (_Y_D - _Y_A);
  const back  = k === 1 ? _COL_BACK_1  : _COL_BACK_2;
  const front = k === 1 ? _COL_FRONT_1 : _COL_FRONT_2;
  return back + (front - back) * t;
};

// x-position of the outer trapezoid edge (side = 'L' or 'R') at height y.
const _edgeX = (side, y) => {
  const t = (y - _Y_A) / (_Y_D - _Y_A);
  const top = side === 'L' ? _TL[0] : _TR[0];
  const bot = side === 'L' ? _BL[0] : _BR[0];
  return top + (bot - top) * t;
};

// Build one cell polygon by combining the row-y edges with the column-x
// lines. `col` is 0 (cell left col), 1 (cell middle col), 2 (cell right col).
const _cell = (yTop, yBot, col) => {
  const xTL = col === 0 ? _edgeX('L', yTop) : _colDivX(col, yTop);
  const xTR = col === 2 ? _edgeX('R', yTop) : _colDivX(col + 1, yTop);
  const xBR = col === 2 ? _edgeX('R', yBot) : _colDivX(col + 1, yBot);
  const xBL = col === 0 ? _edgeX('L', yBot) : _colDivX(col, yBot);
  return [[xTL, yTop], [xTR, yTop], [xBR, yBot], [xBL, yBot]];
};

const GRID_CELLS = {
  1: { poly: _cell(_Y_A, _Y_B, 0) },
  2: { poly: _cell(_Y_A, _Y_B, 1) },
  3: { poly: _cell(_Y_A, _Y_B, 2) },
  4: { poly: _cell(_Y_B, _Y_C, 0) },
  5: { poly: _cell(_Y_B, _Y_C, 1) },
  6: { poly: _cell(_Y_B, _Y_C, 2) },
  7: { poly: _cell(_Y_C, _Y_D, 0) },
  8: { poly: _cell(_Y_C, _Y_D, 1) },
  9: { poly: _cell(_Y_C, _Y_D, 2) },
};

// Presenter foot anchor per cell — horizontal centre of the cell at its
// FRONT (bottom) row Y, then LIFTED 10 px upward so the feet visually
// stand INSIDE the polygon instead of directly on the tape line.
const FEET_LIFT = 10;
const _anchor = (yBot, col, scale) => {
  const xL = col === 0 ? _edgeX('L', yBot) : _colDivX(col, yBot);
  const xR = col === 2 ? _edgeX('R', yBot) : _colDivX(col + 1, yBot);
  return { fx: (xL + xR) / 2, fy: yBot - FEET_LIFT, scale, isGrid: true };
};
const CELL_ANCHORS = {
  1: _anchor(_Y_B, 0, 0.70),
  2: _anchor(_Y_B, 1, 0.70),
  3: _anchor(_Y_B, 2, 0.70),
  4: _anchor(_Y_C, 0, 0.85),
  5: _anchor(_Y_C, 1, 0.85),
  6: _anchor(_Y_C, 2, 0.85),
  7: _anchor(_Y_D, 0, 1.00),
  8: _anchor(_Y_D, 1, 1.00),
  9: _anchor(_Y_D, 2, 1.00),
};

// Convert an image-pixel anchor into viewport-% for the presenter render.
// Off-stage / walk-off use plain viewport % directly.
const cellAnchor = (id) => {
  const a = CELL_ANCHORS[id];
  return {
    cx:    (a.fx / GRID_VB_W) * 100,
    footY: (a.fy / GRID_VB_H) * 100,
    scale: a.scale,
    isGrid: true,
  };
};

// DEV-ONLY outline of the polygons so we can verify tape alignment. Flip to
// `false` (or delete) once the 9 cells match the baked grid exactly.
const DEBUG_GRID = false;

// Off-stage right — starting position beside the RIGHT of the grid.
const OFFSTAGE_RIGHT = { cx: 95, footY: 95, scale: 1.0 };
// Off-stage right exit — used for the final walking exit out through
// the RIGHT edge of the room.
const OFFSTAGE_RIGHT_EXIT = { cx: 115, footY: 95, scale: 1.0 };
// Off-stage left — kept for potential future re-use / replay.
const OFFSTAGE_LEFT  = { cx: -8, footY: 95, scale: 1.0 };

// Base container height (in % of scene) for scale 1.0. Scales linearly with
// the per-row perspective factor.
const BASE_PRESENTER_HEIGHT = 102;

// The 1400px asset set (Sep 2026) has ~6.2% blank padding above AND below
// the actual figure (measured from the PNG alpha channel) — used to hide a
// small baked margin. Left uncompensated, this reads as the character
// floating above the cell. Shift the whole box down by that fraction of its
// own height so the VISIBLE foot line lands exactly on the cell's front edge.
const MASK_GAP_FRACTION = 0.062;

// Convert a cell anchor into left/bottom/height CSS. We center the presenter
// horizontally on `cx` (via translateX(-50%) at the render site) and anchor
// his feet to the FRONT edge of the cell (`bottom = 100 - footY`). Height is
// scaled per row so back-row cells (70%) read smaller than front-row (100%).
// `gridDrop` was a FIXED 45px correction (tuned against a ~900px-tall
// desktop viewport, i.e. 5% of that height) that visually grounds the feet
// a touch lower. As a flat pixel value it's a much BIGGER fraction of a
// short mobile-landscape viewport (e.g. 45px is ~12% of a 375px-tall
// screen instead of ~5%), which was dragging the presenter almost a full
// row further back/up than the cell he'd actually walked to. Expressed in
// `dvh` on mobile it stays the same ~5% proportion at any viewport height,
// matching the desktop feel exactly instead of amplifying with a shorter
// screen. Desktop keeps the original literal `45px` untouched.
const anchorToStyle = (a, isMobile) => {
  const h = BASE_PRESENTER_HEIGHT * a.scale;
  const gridDrop = a.isGrid ? (isMobile ? "5dvh" : "45px") : "0px";
  return {
    left:   `${a.cx}%`,
    bottom: `calc(${100 - a.footY - h * MASK_GAP_FRACTION}% - ${gridDrop})`,
    height: `${h}%`,
  };
};

// ---------- COMPONENT -------------------------------------------------------
export function RehearsalRoomSpace() {
  const {
    answers,
    setRoomAnswer,
    setTagsForRoom,
    setCanProceed,
    setOverrideMessage,
    setBeforeNext,
    setReflection,
    selectedPresenter,
    overrideMessage,
    next,
  } = useAudit();
  const isMobile = useIsMobileAudit();
  // Off-stage right anchor — desktop keeps the figure at cx:95 (mostly
  // off-canvas, about to walk in). On a narrow phone landscape screen the
  // Presenter's own width is a much larger fraction of the container, so
  // that same cx pushes him almost entirely out of view for the whole
  // intro. Pulled in on mobile only; every ON-grid cell anchor is
  // untouched so the walk-to-cell feedback still works correctly.
  const OFF_RIGHT      = isMobile ? { cx: 80, footY: 95, scale: 1.0 } : OFFSTAGE_RIGHT;
  const OFF_RIGHT_EXIT = isMobile ? { cx: 104, footY: 95, scale: 1.0 } : OFFSTAGE_RIGHT_EXIT;

  const a = answers.space || {};
  const introStep          = a.introStep          ?? 0;
  const qStep              = a.qStep              ?? 0;
  const qBubbleStep        = a.qBubbleStep        ?? 0;
  const positions          = a.positions          || {};
  const replayIntroStep    = a.replayIntroStep    ?? 0;
  const reflectIntroStep   = a.reflectIntroStep   ?? 0;
  const reflectionPick     = a.reflection?.selected ?? null;
  const outroStep          = a.outroStep          ?? 0;
  const replayDone         = a.replayDone         === true;

  // Local (non-persisted) state for animated replay.
  const [replayActive, setReplayActive] = useState(false);
  const [replayCellIndex, setReplayCellIndex] = useState(-1); // -1 = off-stage
  const [replayLabel, setReplayLabel] = useState(null);       // label bubble text

  // True once the user has clicked Continue after the outro — presenter
  // walks off to the LEFT then Room 05 advances to Room 06.
  const [exiting, setExiting] = useState(false);

  // Presenter local state — pose + flip are computed per-move so we can flip
  // during walking without also flipping the standing pose.
  const [pose, setPose] = useState("neutral");
  const [flip, setFlip] = useState(false);
  const walkTimer = useRef(null);

  // ---------- Phase machine helpers ----------------------------------------
  const introDone   = introStep >= INTRO_STEPS.length;
  const currentMoment = MOMENTS[qStep] || null;
  const currentQuestionBubbles = currentMoment ? Q_STEPS[currentMoment] : EMPTY_BUBBLES;
  const questionsDone = qStep >= MOMENTS.length;   // all 4 positions chosen

  const inIntroPhase       = !introDone;
  const inQuestionBubble   = introDone && !questionsDone && qBubbleStep < currentQuestionBubbles.length;
  const inQuestionPick     = introDone && !questionsDone && qBubbleStep >= currentQuestionBubbles.length;
  const replayIntroDone    = replayIntroStep >= REPLAY_INTRO_STEPS.length;
  const inReplayIntro      = questionsDone && !replayIntroDone;
  const inReplay           = questionsDone && replayIntroDone && !replayDone;
  const reflectIntroDone   = reflectIntroStep >= REFLECTION_INTRO_STEPS.length;
  const inReflectionIntro  = questionsDone && replayDone && !reflectIntroDone;
  const inReflectionPick   = questionsDone && replayDone && reflectIntroDone && !reflectionPick;
  const outroDone          = outroStep >= OUTRO_STEPS.length;
  const inOutro            = questionsDone && replayDone && reflectionPick && !outroDone;
  const isLeaving          = questionsDone && replayDone && reflectionPick && outroDone;

  // Rooms 01-04 pattern — while an in-scene bubble is active the whole
  // screen becomes a "tap-to-continue" catcher.
  const inCatcher = inIntroPhase || inQuestionBubble || inReplayIntro || inReflectionIntro || inOutro;

  // ---------- Bubble + Continue-gate sync ----------------------------------
  useEffect(() => {
    if (inIntroPhase)        { setOverrideMessage(INTRO_STEPS[introStep]);                       setCanProceed(false); return; }
    if (inQuestionBubble)    { setOverrideMessage(currentQuestionBubbles[qBubbleStep]);          setCanProceed(false); return; }
    if (inQuestionPick)      { setOverrideMessage("");                                           setCanProceed(false); return; }
    if (inReplayIntro)       { setOverrideMessage(REPLAY_INTRO_STEPS[replayIntroStep]);          setCanProceed(false); return; }
    if (inReplay)            { setOverrideMessage("");                                           setCanProceed(false); return; }
    if (inReflectionIntro)   { setOverrideMessage(REFLECTION_INTRO_STEPS[reflectIntroStep]);     setCanProceed(false); return; }
    if (inReflectionPick)    { setOverrideMessage("");                                           setCanProceed(false); return; }
    if (inOutro)             { setOverrideMessage(OUTRO_STEPS[outroStep]);                       setCanProceed(false); return; }
    // Journey complete — enable Continue.
    setOverrideMessage(OUTRO_STEPS[OUTRO_STEPS.length - 1]);
    setCanProceed(true);
  }, [inIntroPhase, introStep, inQuestionBubble, qBubbleStep, currentQuestionBubbles, inQuestionPick,
      inReplayIntro, replayIntroStep, inReplay, inReflectionIntro, reflectIntroStep, inReflectionPick,
      inOutro, outroStep, setCanProceed, setOverrideMessage]);

  // ---------- Continue interceptor for the walking exit --------------------
  useEffect(() => {
    setBeforeNext(() => {
      if (!isLeaving) return false;
      // Trigger the walk-off animation, THEN advance after it completes.
      setPose("walking");
      setFlip(false); // moving right → don't flip (walk-right = natural)
      setExiting(true);
      setTimeout(() => { setBeforeNext(null); next(); }, 1200);
      return false;
    });
    return () => setBeforeNext(null);
  }, [isLeaving, setBeforeNext, next]);

  // ---------- Persist granular raw tags ------------------------------------
  useEffect(() => {
    const t = [];
    MOMENTS.forEach((m) => {
      if (positions[m]) t.push(`space:${m}:${positions[m]}`);
    });
    setTagsForRoom("space", t);
  }, [positions.opening, positions.tension, positions.video, positions.cta, setTagsForRoom]);

  // ---------- Presenter position (target) ----------------------------------
  // For Q1..Q4 phases we snap the presenter to the last chosen cell.
  // During replay we drive it from replayCellIndex.
  // On leaving, we walk him off to the LEFT.
  const targetAnchor = (() => {
    if (exiting)               return OFF_RIGHT_EXIT;
    if (inReplay || replayActive) {
      if (replayCellIndex < 0) return OFF_RIGHT;
      const cellId = positions[MOMENTS[replayCellIndex]];
      return cellId ? cellAnchor(cellId) : OFF_RIGHT;
    }
    // Question phases — last answered moment (or off-stage right if none yet).
    const lastAnswered = MOMENTS.slice(0, qStep).reverse().find((m) => positions[m]);
    if (lastAnswered) return cellAnchor(positions[lastAnswered]);
    return OFF_RIGHT;
  })();

  // ---------- REPLAY driver ------------------------------------------------
  useEffect(() => {
    if (!inReplay || replayActive) return;
    let cancelled = false;
    setReplayActive(true);
    setReplayCellIndex(-1);
    setReplayLabel(null);

    const runStep = (i) => {
      if (cancelled) return;
      if (i >= MOMENTS.length) {
        // Replay finished — a brief pause, then mark done.
        setTimeout(() => {
          if (cancelled) return;
          setReplayLabel(null);
          setReplayActive(false);
          setRoomAnswer("space", { replayDone: true });
        }, 700);
        return;
      }
      // Skip the walking pose entirely — glide the presenter to the next
      // stop already in the moment's standing pose so he never "walks" on
      // the grid during the replay.
      setFlip(false);
      setPose(MOMENT_POSE[MOMENTS[i]]);
      setReplayLabel(null);
      setReplayCellIndex(i);
      // Match the motion transition duration, then show the label bubble
      // and continue to the next stop.
      walkTimer.current = setTimeout(() => {
        if (cancelled) return;
        setReplayLabel(REPLAY_LABELS[MOMENTS[i]]);
        walkTimer.current = setTimeout(() => { runStep(i + 1); }, 1400);
      }, 1250);
    };

    // Small delay so the audience-side transition into replay feels intentional.
    walkTimer.current = setTimeout(() => runStep(0), 500);
    return () => {
      cancelled = true;
      if (walkTimer.current) clearTimeout(walkTimer.current);
    };
  }, [inReplay]);

  // ---------- Standing pose when a Q is answered but we're not replaying ---
  useEffect(() => {
    if (exiting || inReplay || replayActive) return;
    const lastAnsweredIdx = MOMENTS.slice(0, qStep).reverse()
      .findIndex((m) => positions[m]);
    if (lastAnsweredIdx === -1) {
      setPose("neutral");
      setFlip(false);
      return;
    }
    const lastMoment = MOMENTS.slice(0, qStep).reverse()[lastAnsweredIdx];
    // Pose = communication pose for the last answered moment.
    setPose(MOMENT_POSE[lastMoment]);
    setFlip(false);
  }, [qStep, positions.opening, positions.tension, positions.video, positions.cta,
      exiting, inReplay, replayActive]);

  // ---------- Handlers ------------------------------------------------------
  const advanceCatcher = () => {
    if (inIntroPhase)      return setRoomAnswer("space", { introStep: introStep + 1 });
    if (inQuestionBubble)  return setRoomAnswer("space", { qBubbleStep: qBubbleStep + 1 });
    if (inReplayIntro)     return setRoomAnswer("space", { replayIntroStep: replayIntroStep + 1 });
    if (inReflectionIntro) return setRoomAnswer("space", { reflectIntroStep: reflectIntroStep + 1 });
    if (inOutro)           return setRoomAnswer("space", { outroStep: outroStep + 1 });
  };

  const pickCell = (cellId) => {
    if (!inQuestionPick) return;
    tapFeedback();
    const moment = currentMoment;
    // Skip the walking pose entirely — glide the presenter to the new
    // cell in the moment's standing pose so he never "walks" on the grid.
    setPose(MOMENT_POSE[moment]);
    setFlip(false);

    // Save the choice + advance.
    const nextPositions = { ...positions, [moment]: cellId };
    setRoomAnswer("space", {
      positions: nextPositions,
      qStep: qStep + 1,
      qBubbleStep: 0,
      path: MOMENTS.filter((m) => nextPositions[m]).map((m) => nextPositions[m]),
    });
  };

  const pickReflection = (optionId) => {
    if (!inReflectionPick) return;
    const tag = `space_reflection:${optionId}`;
    setRoomAnswer("space", {
      reflection: { selected: optionId, tag },
      outroStep: 0,
    });
    // Shared global reflections bucket — for future Pattern Engine.
    setReflection("space", {
      question: REFLECTION_QUESTION,
      selected: optionId,
      tag,
    });
    setTagsForRoom("space_reflection", [tag]);
  };

  // ---------- Presenter render ---------------------------------------------
  const presenter = selectedPresenter;
  const poseFile = presenter?.poses?.[pose] || presenter?.poses?.neutral;
  const presenterSrc = presenter ? `${presenter.avatarBase}/${poseFile}` : "";

  // ---------- Projector video state ----------------------------------------
  // Active while the user is picking during Q3, AND during the replay video stop.
  const videoActive =
    (inQuestionPick && currentMoment === "video") ||
    (replayActive && replayCellIndex === 2);

  // ---------- Grid interactivity -------------------------------------------
  const gridInteractive = inQuestionPick;
  const selectedCellForCurrent = currentMoment ? positions[currentMoment] : null;

  // Highlight the cell the presenter is currently in (or heading to).
  const activeCellId = (() => {
    if (exiting) return null;
    if (replayActive && replayCellIndex >= 0)
      return positions[MOMENTS[replayCellIndex]];
    const lastAnswered = MOMENTS.slice(0, qStep).reverse().find((m) => positions[m]);
    return lastAnswered ? positions[lastAnswered] : null;
  })();

  // ---------- Bubble helpers ------------------------------------------------
  const showLabelBubble = replayActive && replayLabel;
  const showMainBubble = !showLabelBubble && overrideMessage && !inQuestionPick && !inReflectionPick;

  // Speech bubble follows the presenter — always to the LEFT of his head,
  // offset by roughly half his silhouette width so the bubble never sits
  // ON TOP of his body. The right edge of the bubble stops well before
  // his left shoulder; a min-left clamp keeps it inside the viewport on
  // narrow screens.
  const _bubbleAnchor = (() => {
    const cx = targetAnchor.cx;                           // 0..100 (%)
    const headYPct = Math.max(3, targetAnchor.footY - BASE_PRESENTER_HEIGHT * targetAnchor.scale - 1);
    // Presenter silhouette half-width in viewport %:
    //   presenter aspect = 1122 / 1402 ≈ 0.80
    //   presenter render height (vh) = BASE_PRESENTER_HEIGHT * scale
    //   width (vh) = height * 0.80; convert to vw by *(1080/1920) = *0.5625
    const halfWidthVW = (BASE_PRESENTER_HEIGHT * targetAnchor.scale * 0.80 * 0.5625) / 2;
    const gap = 3;                                        // extra breathing room in %
    const rawLeft = cx - halfWidthVW - gap;
    // Clamp so at least a sliver of the bubble stays on-screen.
    return { top: headYPct, left: Math.max(28, rawLeft) };
  })();
  const bubbleStyle = {
    top: `${_bubbleAnchor.top}%`,
    left: `${_bubbleAnchor.left}%`,
    transform: "translateX(-100%)",
    zIndex: 100,
    maxWidth: 440,
  };
  const _tailStyle = { right: 24, bottom: -16, left: "auto" };

  const catcherStep = inIntroPhase       ? introStep + 1
                     : inQuestionBubble   ? qBubbleStep + 1
                     : inReplayIntro      ? replayIntroStep + 1
                     : inReflectionIntro  ? reflectIntroStep + 1
                     : outroStep + 1;
  const catcherTotal = inIntroPhase       ? INTRO_STEPS.length
                     : inQuestionBubble   ? currentQuestionBubbles.length
                     : inReplayIntro      ? REPLAY_INTRO_STEPS.length
                     : inReflectionIntro  ? REFLECTION_INTRO_STEPS.length
                     : OUTRO_STEPS.length;

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="rehearsal-room-space" style={{ background: "#141310" }}>
      {/* Background — mobile background-size is forced to "100% 100%" via
          audit-mobile.css (`.space-bg-layer` rule) so it matches the SVG
          grid's non-uniform stretch (preserveAspectRatio="none") exactly. */}
      <div aria-hidden="true" className="absolute inset-0 space-bg-layer"
        style={{ backgroundImage: "url(/audit/bg_space.webp)", backgroundSize: "cover", backgroundPosition: "center" }} />

      {/* Subtle scrim */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
        style={{ background: "rgba(10,8,5,0.20)", zIndex: 5 }} />

      {/* Projector "screen" — muted animated placeholder when active */}
      <AnimatePresence>
        {videoActive && (
          <motion.div
            key="projector-video"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute pointer-events-none"
            style={{ left: "39.4%", top: "17.6%", width: "31.9%", height: "31.9%", zIndex: 8 }}
            data-testid="space-projector-video"
          >
            <div className="w-full h-full relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, rgba(80,60,30,0.35) 0%, rgba(120,90,40,0.55) 100%)",
                mixBlendMode: "screen",
              }}
            >
              {/* Slow shimmering "video" — pure CSS. */}
              <div className="absolute inset-0"
                style={{
                  background: "linear-gradient(115deg, transparent 0%, rgba(255,220,150,0.35) 35%, rgba(255,235,190,0.55) 50%, rgba(255,220,150,0.35) 65%, transparent 100%)",
                  backgroundSize: "220% 100%",
                  animation: "space-shimmer 4.5s linear infinite",
                }}
              />
              <div className="absolute inset-0"
                style={{
                  background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 3px)",
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyframes for shimmer */}
      <style>{`@keyframes space-shimmer { 0% { background-position: -60% 0; } 100% { background-position: 160% 0; } }`}</style>

      {/* ---------- SVG floor grid — traced onto the baked tape marks ---- */}
      <svg
        viewBox={`0 0 ${GRID_VB_W} ${GRID_VB_H}`}
        preserveAspectRatio="none"
        className="absolute inset-0"
        style={{ width: "100%", height: "100%", zIndex: 10, pointerEvents: "none" }}
        data-testid="space-grid"
      >
        {Object.entries(GRID_CELLS).map(([id, cell]) => {
          const cellId = Number(id);
          const isSelectedForCurrent = selectedCellForCurrent === cellId;
          const isActive = activeCellId === cellId;
          const points = cell.poly.map((p) => `${p[0]},${p[1]}`).join(" ");
          const anchor = CELL_ANCHORS[cellId];
          return (
            <g key={cellId}>
              <polygon
                points={points}
                onClick={() => gridInteractive && pickCell(cellId)}
                data-testid={`space-cell-${cellId}`}
                style={{
                  pointerEvents: gridInteractive ? "auto" : "none",
                  cursor: gridInteractive ? "pointer" : "default",
                  fill: isSelectedForCurrent
                    ? "rgba(218,152,60,0.42)"          // #da983c strong
                    : isActive
                    ? "rgba(218,152,60,0.22)"          // presenter is here
                    : DEBUG_GRID
                    ? "rgba(255,60,140,0.08)"          // dev outline fill
                    : "transparent",                    // default: transparent
                  stroke: isSelectedForCurrent
                    ? "#c25a3a"                         // terracotta accent
                    : DEBUG_GRID
                    ? "rgba(255,60,140,0.90)"
                    : "transparent",
                  strokeWidth: isSelectedForCurrent ? 3 : (DEBUG_GRID ? 3 : 0),
                  transition: "fill 250ms ease, stroke 250ms ease",
                }}
                className="space-cell"
              />
              {DEBUG_GRID && anchor && (
                <>
                  <circle cx={anchor.fx} cy={anchor.fy} r={7}
                    fill="#00ffe0" stroke="#003b3b" strokeWidth={2} pointerEvents="none" />
                  <text
                    x={anchor.fx}
                    y={anchor.fy - 14}
                    textAnchor="middle"
                    style={{
                      fill: "#ffffff",
                      fontFamily: "monospace",
                      fontSize: 20,
                      fontWeight: 700,
                      paintOrder: "stroke",
                      stroke: "#000",
                      strokeWidth: 4,
                      strokeLinejoin: "round",
                      pointerEvents: "none",
                      userSelect: "none",
                    }}
                  >
                    {cellId}
                  </text>
                </>
              )}
            </g>
          );
        })}

        <style>{`
          .space-cell:hover { fill: rgba(218,152,60,0.22) !important; }
        `}</style>
      </svg>

      {/* ---------- Presenter --------------------------------------------- */}
      {presenter && (
        <motion.div
          className="absolute"
          initial={{
            left:   "110%",
            bottom: anchorToStyle(OFF_RIGHT, isMobile).bottom,
            height: anchorToStyle(OFF_RIGHT, isMobile).height,
            opacity: 1,
          }}
          animate={{
            left:   anchorToStyle(targetAnchor, isMobile).left,
            bottom: anchorToStyle(targetAnchor, isMobile).bottom,
            height: anchorToStyle(targetAnchor, isMobile).height,
            opacity: exiting ? 0 : 1,
          }}
          transition={{ duration: exiting ? 1.1 : 1.2, ease: EASE }}
          style={{
            zIndex: 30,
            transform: "translateX(-50%)",
            // Presenter PNG intrinsic size is 1122×1402 (ratio ~0.80). Set
            // aspect-ratio explicitly so the container width derives from
            // its animated height — otherwise `img { width:auto }` inside a
            // shrink-to-fit container collapses to a narrow strip.
            aspectRatio: "1122 / 1402",
            // Never intercept clicks — the SVG grid beneath must remain
            // clickable when the presenter is standing on/over a cell.
            pointerEvents: "none",
          }}
          data-testid="space-presenter"
        >
          <PresenterFloorShadow width="50%" height="9%" offsetX="7%" offsetY="1%" />
          <img
            src={presenterSrc}
            alt={presenter.name}
            draggable={false}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="select-none block"
            style={{
              height: "100%",
              width: "100%",
              objectFit: "contain",
              transform: `scaleX(${flip ? 1 : -1})`,
              transition: "transform 350ms ease",
              filter: "brightness(0.95) sepia(0.1)",
              // Erase the baked white shadow ellipse under the presenter's
              // feet by masking off the very bottom slice of the PNG.
              WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 95%, transparent 100%)",
              maskImage:       "linear-gradient(to bottom, black 0%, black 95%, transparent 100%)",
            }}
            data-testid={`space-presenter-img-${pose}`}
          />
        </motion.div>
      )}

      {/* ---------- Corner brand mark ------------------------------------- */}
      <div className="absolute right-6 top-5 flex flex-col items-end pointer-events-none" style={{ zIndex: 50 }}>
        <p className="font-serif italic text-paper text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          InterAct Globally
        </p>
        <p className="font-hand text-mustard text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          the rehearsal room
        </p>
      </div>

      {/* ---------- Main coaching bubble (follows presenter) -------------- */}
      <AnimatePresence initial={false}>
        {showMainBubble && (
          <motion.div
            key={overrideMessage}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="absolute"
            style={bubbleStyle}
            data-testid="space-bubble"
          >
            <div className="relative bg-white text-[#1B1B18] px-6 py-4 shadow-[0_16px_40px_rgba(0,0,0,0.4)]" style={{ borderRadius: 26 }}>
              <p className="font-serif text-lg leading-snug">{overrideMessage}</p>
              <span aria-hidden="true" className="absolute"
                style={{ ..._tailStyle, width: 0, height: 0,
                  borderLeft: "16px solid transparent", borderRight: "10px solid transparent",
                  borderTop: "20px solid #ffffff", filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))" }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- Replay stop bubble (follows presenter) ---------------- */}
      <AnimatePresence>
        {showLabelBubble && (
          <motion.div
            key={`replay-label-${replayLabel.heading}`}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="absolute"
            style={bubbleStyle}
            data-testid="space-replay-label"
          >
            <div className="relative bg-white text-[#1B1B18] px-6 py-4 shadow-[0_16px_40px_rgba(0,0,0,0.4)]" style={{ borderRadius: 26 }}>
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-terracotta mb-1.5">
                {replayLabel.heading}
              </p>
              <p className="font-serif text-base leading-snug">{replayLabel.sub}</p>
              <span aria-hidden="true" className="absolute"
                style={{ ..._tailStyle, width: 0, height: 0,
                  borderLeft: "16px solid transparent", borderRight: "10px solid transparent",
                  borderTop: "20px solid #ffffff", filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))" }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- Reflection cards -------------------------------------- */}
      <AnimatePresence>
        {inReflectionPick && (
          <motion.div
            key="reflection-cards"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="absolute grid grid-cols-2 gap-4"
            style={{ top: "36%", left: "22%", right: "6%", zIndex: 40 }}
            data-testid="space-reflection-cards"
          >
            {REFLECTION_OPTIONS.map((opt) => {
              const selected = reflectionPick === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => pickReflection(opt.id)}
                  data-testid={`space-reflection-${opt.id}`}
                  className="text-left px-6 py-5 transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    background: selected
                      ? "linear-gradient(180deg, #E9C88A 0%, #B9832C 100%)"
                      : "rgba(242, 235, 218, 0.90)",
                    border: selected
                      ? "2px solid #90533b"
                      : "1px solid rgba(90,60,25,0.16)",
                    boxShadow: selected
                      ? "0 14px 40px rgba(144,83,59,0.35)"
                      : "0 6px 18px rgba(0,0,0,0.16)",
                    color: "#1B1B18",
                  }}
                >
                  <p className="font-serif text-xl md:text-2xl leading-snug">{opt.label}</p>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- Tap catcher for bubble phases ------------------------- */}
      {inCatcher && (
        <button
          type="button"
          onClick={advanceCatcher}
          data-testid="space-advance"
          aria-label="Continue"
          className="absolute inset-0 cursor-pointer bg-transparent pointer-events-auto"
          style={{ zIndex: 88 }}
        >
          <span aria-hidden="true"
            className="absolute bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-paper/70 animate-pulse">
            tap to continue &nbsp;·&nbsp; {catcherStep} / {catcherTotal}
          </span>
        </button>
      )}
    </div>
  );
}

export default RehearsalRoomSpace;
